import {
  apiFetch,
  downloadAuthenticatedFile,
} from "../api/client";
import {
  getExplicitSelectedCompanyId,
} from "../utils/tenantContext";
import PenTestForms from "./PenTestForms";
import React, { useEffect, useState } from "react";

function PenTestPage() {
  const companyId = getExplicitSelectedCompanyId();

  const tenantPath = (path) => {
    if (!companyId) {
      return null;
    }

    const separator = path.includes("?") ? "&" : "?";

    return `${path}${separator}company_id=${encodeURIComponent(
      companyId
    )}`;
  };

  const [dashboard, setDashboard] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [targets, setTargets] = useState([]);
  const [findings, setFindings] = useState([]);
  const [scans, setScans] = useState([]);
  const [remediation, setRemediation] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("All");
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  function loadData() {
    if (!companyId) {
      console.warn(
        "Select a tenant before loading Pen Test data."
      );
      return;
    }

    Promise.all([
      apiFetch(tenantPath(`/pentest/dashboard`)).then((r) => r.json()),
      apiFetch(tenantPath(`/pentest/engagements`)).then((r) => r.json()),
      apiFetch(tenantPath(`/pentest/targets`)).then((r) => r.json()),
      apiFetch(tenantPath(`/pentest/findings`)).then((r) => r.json()),
      apiFetch(tenantPath(`/pentest/scans`)).then((r) => r.json()),
      apiFetch(tenantPath(`/pentest/remediation`)).then((r) => r.json()),
      apiFetch(tenantPath(`/pentest/profiles`)).then((r) => r.json()),
    ])
      .then(([dash, eng, targ, findingData, scanData, remediationData, profileData]) => {
        const rows = findingData.findings || [];
        setDashboard(dash);
        setEngagements(eng.engagements || []);
        setTargets(targ.targets || []);
        setFindings(rows);
        setScans(scanData.scans || []);
        setRemediation(remediationData.remediation || []);
      setProfiles(profileData.profiles || []);
        setSelected((current) => {
          if (!current) return rows[0] || null;
          return rows.find((r) => r.id === current.id) || rows[0] || null;
        });
      })
      .catch((err) => console.error("Pen Test module fetch error:", err));
  }

  function updateRemediation(findingId, status) {
    const item = remediation.find((r) => r.finding_id === findingId);

    if (!item) {
      console.warn("No remediation item found for finding:", findingId);
      return;
    }


    if (!companyId) {
      window.alert(
        "Select a tenant before updating remediation."
      );
      return;
    }

    apiFetch(tenantPath(`/pentest/remediation/${item.id}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        status,
        assigned_to:
          localStorage.getItem("username") ||
          "authenticated_admin",
      }),
    })
      .then((r) => r.json())
      .then(() => loadData())
      .catch((err) => console.error("Remediation update error:", err));
  }


  async function exportPdfReport() {
    if (!companyId) {
      window.alert(
        "Select a tenant before exporting the report."
      );
      return;
    }

    try {
      await downloadAuthenticatedFile(
        tenantPath(`/pentest/report/pdf`),
        "congo-market-ai-pentest-report.pdf"
      );
    } catch (error) {
      console.error(
        "Pen Test PDF export error:",
        error
      );

      window.alert(
        error.message ||
          "Unable to export Pen Test PDF report."
      );
    }
  }

  const activeEngagement = engagements[0];
  const activeTarget = targets[0];

  const filtered =
    filter === "All"
      ? findings
      : findings.filter(
          (f) => String(f.severity || "").toLowerCase() === filter.toLowerCase()
        );

  const counts = dashboard?.findings || {
    total: findings.length,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
  };

  const selectedRemediation = selected
    ? remediation.find((r) => r.finding_id === selected.id)
    : null;

  return (
    <div className="pentest-page">
      <section className="pentest-hero">
        <div>
          <p className="eyebrow">Authorized Ethical Assessment</p>
          <h1>Congo Market AI Pen Test</h1>
          <p>
            Engagement-based penetration testing workspace with authorization,
            scope, targets, evidence, remediation, and risk scoring.
          </p>

          <button className="report-btn" onClick={exportPdfReport}>
            Export PDF Report
            </button>


        </div>

        <div className="score-orb">
          <span>{dashboard?.security_score ?? 100}</span>
          <small>Security Score</small>
        </div>
      </section>

      <section className="pentest-metrics">
        <Metric title="Total Findings" value={counts.total || 0} />
        <Metric title="Critical" value={counts.critical || 0} severity="critical" />
        <Metric title="High" value={counts.high || 0} severity="high" />
        <Metric title="Medium" value={counts.medium || 0} severity="medium" />
        <Metric title="Engagements" value={engagements.length || 0} />
        <Metric title="Targets" value={targets.length || 0} />
      </section>

      <section className="pentest-scope-grid">
        <div className="scope-card">
          <h2>Active Engagement</h2>
          {activeEngagement ? (
            <>
              <Info label="Name" value={activeEngagement.name} />
              <Info label="Client" value={activeEngagement.client_name} />
              <Info label="Authorized By" value={activeEngagement.authorized_by} />
              <Info label="Tester" value={activeEngagement.tester_name} />
              <Info label="Status" value={activeEngagement.status} />

              <div className="scope-text">
                <strong>Rules of Engagement</strong>
                <p>{activeEngagement.rules_of_engagement || "—"}</p>
              </div>

              <div className="scope-text">
                <strong>Scope Summary</strong>
                <p>{activeEngagement.scope_summary || "—"}</p>
              </div>
            </>
          ) : (
            <p>No engagement created yet.</p>
          )}
        </div>

        <div className="scope-card">
          <h2>Primary Target</h2>
          {activeTarget ? (
            <>
              <Info label="Hostname" value={activeTarget.hostname} />
              <Info label="Type" value={activeTarget.target_type} />
              <Info label="Device UUID" value={activeTarget.device_uuid} />
              <Info label="Approved Ports" value={activeTarget.approved_ports} />
              <Info label="Owner" value={activeTarget.owner} />
              <Info label="Status" value={activeTarget.status} />
            </>
          ) : (
            <p>No target registered yet.</p>
          )}
        </div>
      </section>
 
      <PenTestForms engagements={engagements} onSaved={loadData} />
 
      <section className="module-panel">
        <h2>Assessment Profiles</h2>

        <div className="profile-grid">
          {profiles.map((p) => (
            <div className="profile-card" key={p.id}>
              <strong>{p.name}</strong>
              <p>{p.description}</p>
              <span>{p.enabled ? "Enabled" : "Disabled"}</span>
            </div>
          ))}

          {profiles.length === 0 && <p>No profiles loaded.</p>}
        </div>
      </section>

      <section className="pentest-console">
        <div className="console-left">
          <div className="console-toolbar">
            <h2>Findings</h2>
            <button onClick={loadData}>Refresh</button>
          </div>

          <div className="filter-row">
            {["All", "Critical", "High", "Medium", "Low"].map((level) => (
              <button
                key={level}
                className={filter === level ? "filter-chip active" : "filter-chip"}
                onClick={() => setFilter(level)}
              >
                {level}
              </button>
            ))}
          </div>

          <div className="findings-stream">
            {filtered.map((f) => (
              <div
                key={f.id}
                className={
                  selected?.id === f.id
                    ? "finding-stream-item selected"
                    : "finding-stream-item"
                }
                onClick={() => setSelected(f)}
              >
                <div className="stream-top">
                  <span
                    className={`severity-light ${String(
                      f.severity || "low"
                    ).toLowerCase()}`}
                  />
                  <strong>{f.title}</strong>
                  <span className="risk-score">{f.risk_score || 0}</span>
                </div>

                <p>{f.evidence}</p>

                <div className="stream-bottom">
                  <span>{f.hostname || "Unknown Host"}</span>
                  <span>{f.status || "Open"}</span>
                </div>
              </div>
            ))}

            {filtered.length === 0 && <div className="empty-module">No findings.</div>}
          </div>
        </div>

        <div className="console-right">
          {selected ? (
            <>
              <div className="detail-header">
                <span
                  className={`severity-badge ${String(
                    selected.severity || "low"
                  ).toLowerCase()}`}
                >
                  {selected.severity}
                </span>
                <h2>{selected.title}</h2>
                <p>{selected.hostname}</p>
              </div>

              <DetailBlock title="Full Evidence" code>
                {selected.evidence}
              </DetailBlock>

              <DetailBlock title="Business Impact">
                {selected.business_impact}
              </DetailBlock>

              <DetailBlock title="Recommended Fix">
                {selected.recommendation}
              </DetailBlock>

              <DetailBlock title="Validation Step">
                {selected.validation_step}
              </DetailBlock>

              <div className="intel-grid">
                <Intel label="MITRE ATT&CK" value={selected.mitre_mapping || "Not mapped"} />
                <Intel label="CVE Mapping" value={selected.cve_mapping || "Not mapped"} />
                <Intel label="Scan ID" value={selected.scan_id || "N/A"} />
                <Intel label="Finding Status" value={selected.status || "Open"} />
                <Intel
                  label="Remediation Status"
                  value={selectedRemediation?.status || "Open"}
                />
                <Intel
                  label="Assigned To"
                  value={selectedRemediation?.assigned_to || "Unassigned"}
                />
              </div>

              <div className="remediation-actions">
                <button onClick={() => updateRemediation(selected.id, "Reviewed")}>
                  Mark Reviewed
                </button>
                <button onClick={() => updateRemediation(selected.id, "Fixed")}>
                  Mark Fixed
                </button>
                <button onClick={() => updateRemediation(selected.id, "Verified")}>
                  Mark Verified
                </button>
              </div>
            </>
          ) : (
            <div className="empty-module">Select a finding.</div>
          )}
        </div>
      </section>

      <section className="pentest-bottom">
        <div className="module-panel">
          <h2>Latest Scans</h2>
          {scans.slice(0, 5).map((s) => (
            <div className="mini-row" key={s.id}>
              <span>Scan #{s.id}</span>
              <strong>{s.status}</strong>
              <small>
                {s.completed_at ? new Date(s.completed_at).toLocaleString() : ""}
              </small>
            </div>
          ))}
          {scans.length === 0 && <p>No scans yet.</p>}
        </div>

        <div className="module-panel">
          <h2>Remediation Queue</h2>
          {remediation.slice(0, 5).map((r) => (
            <div className="mini-row" key={r.id}>
              <span>{r.title}</span>
              <strong>{r.status}</strong>
              <small>{r.hostname}</small>
            </div>
          ))}
          {remediation.length === 0 && <p>No remediation items yet.</p>}
        </div>
      </section>
    </div>
  );
}

function Metric({ title, value, severity }) {
  return (
    <div className={`metric-card ${severity || ""}`}>
      <span>{title}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="info-row">
      <span>{label}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}

function DetailBlock({ title, children, code }) {
  return (
    <div className="detail-block">
      <h3>{title}</h3>
      {code ? <pre>{children || "—"}</pre> : <p>{children || "—"}</p>}
    </div>
  );
}

function Intel({ label, value }) {
  return (
    <div className="intel-card">
      <span>{label}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}

function exportReport() {
  const companyId =
    getExplicitSelectedCompanyId();

  if (!companyId) {
    window.alert(
      "Select a tenant before exporting the report."
    );
    return;
  }

  apiFetch(
    `/pentest/report?company_id=${encodeURIComponent(
      companyId
    )}`
  )
    .then((r) => r.json())
    .then((data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "congo-market-ai-pentest-report.json";
      a.click();
      window.URL.revokeObjectURL(url);
    })
    .catch((err) => console.error("Report export error:", err));
}

export default PenTestPage;
